import { LOCATION_TYPE_TITLE, SearchState, Term } from '@/types/models'
import { isNonEmptyObject } from '@/utils/checks'
import React from 'react'
import normalizeSearchState from '@/domain/search/normalizeSearchState'
import { LOCATION_TYPE, UNITED_STATES } from '@/domain/location/constants'
import refineSearchState from '@/domain/search/refineSearchState'
import { navigateToBrowsePageWithQueryParams } from '@/domain/search/navigateToBrowsePageWithQueryParams'
import { shouldDirectToFacetPage } from '@/domain/browseBy/shouldDirectToFacetPage'

export type BrowseItemProps = {
  title: string
  termSlug: string
  slug: string
  count: number
  subtitle?: string
  geoLoc?: string
  customLocationRadiusInMiles?: number
  locationType: LOCATION_TYPE_TITLE
}

export type BrowseLinkItemProps = {
  item: BrowseItemProps
  show: boolean
  searchState: SearchState
  term?: Term
}

export const LinkItem = ({ item, show, searchState, term }) => {
  if (isNonEmptyObject(searchState)) {
    return (
      <BrowseLinkItem
        key={item.title}
        item={item}
        show={show}
        searchState={searchState}
        term={term}
      />
    )
  }
  return (
    <DefaultLinkItem
      key={item.title}
      item={item}
      show={show}
    />
  )
}

export const BrowseLinkItem: React.FC<BrowseLinkItemProps> = ({
  item,
  show,
  searchState,
  term = {} as Term
}) => {
  if (shouldDirectToFacetPage({ item, term })) {
    return (
      <a
        key={item.title}
        href={`/${item.slug}/${item.termSlug}/`}
        className={`${
          show ? 'flex' : 'hidden'
        } flex-col items-start justify-center`}
      >
        <p className="font-medium text-gray-1100">{item.title}</p>
        {item.subtitle && (
          <p className="text-sm text-gray-800">{item.subtitle}</p>
        )}
      </a>
    )
  }
  const handleNavigateToBrowsePage = (
    item: BrowseItemProps,
    searchState: SearchState
  ) => {
    if (item?.locationType === LOCATION_TYPE.CITY && item?.geoLoc) {
      const locationName = [
        item.title,
        searchState.refinementList?.locations?.[0],
        UNITED_STATES
      ]
        .filter(Boolean)
        .join(', ')

      return navigateToBrowsePageWithQueryParams(
        normalizeSearchState({
          ...searchState,
          refinementList: {
            ...searchState.refinementList,
            locations: []
          },
          locationName,
          configure: {
            aroundLatLng: item.geoLoc
          },
          customLocationRadiusInMiles:
            item.customLocationRadiusInMiles?.toString(),
          locationType: item.locationType
        })
      )
    }

    return navigateToBrowsePageWithQueryParams(
      refineSearchState(
        {
          taxonomy: {
            type: 'locations',
            label: item.title
          }
        },
        false
      )
    )
  }

  return (
    <button
      key={item.title}
      className={`flex flex-col items-start justify-center ${
        show ? 'flex' : 'hidden'
      }`}
      onClick={() => handleNavigateToBrowsePage(item, searchState)}
    >
      <p className="font-medium text-gray-1100">{item.title}</p>
      <p className="text-sm text-gray-800">{item.subtitle}</p>
    </button>
  )
}

export const DefaultLinkItem = ({ item, show }) => (
  <a
    key={item.url}
    href={item.url}
    className={`text-sm ${show ? 'block' : 'hidden'}`}
  >
    <p className="font-medium text-gray-1100">{item.title}</p>
    {item.subtitle && <p className="text-sm text-gray-800">{item.subtitle}</p>}
  </a>
)
